import { useParams, Navigate, Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import Seo, { breadcrumbSchema } from "@/components/Seo";
import PageHero from "@/components/shared/PageHero";
import CtaSection from "@/components/shared/CtaSection";
import { Reveal } from "@/components/motion/Reveal";
import { getTerm, GLOSSARY } from "@/data/glossary";

export default function GlossaryTerm() {
  const { slug } = useParams();
  const term = getTerm(slug);
  if (!term) return <Navigate to="/glossary" replace />;
  const related = (term.related || []).map(getTerm).filter(Boolean);

  const defineSchema = {
    "@context": "https://schema.org",
    "@type": "DefinedTerm",
    name: term.term,
    description: term.definition,
    inDefinedTermSet: { "@type": "DefinedTermSet", name: "CompuBrain AI GEO Glossary", url: "https://compubrain.ai/glossary" },
    url: `https://compubrain.ai/glossary/${slug}`,
  };

  return (
    <>
      <Seo
        title={`${term.term} — Definition`}
        description={term.definition}
        path={`/glossary/${slug}`}
        schemas={[defineSchema, breadcrumbSchema([{ name: "Home", path: "/" }, { name: "Glossary", path: "/glossary" }, { name: term.term, path: `/glossary/${slug}` }])]}
      />
      <PageHero
        eyebrow={term.category}
        title={term.term}
        breadcrumbs={[{ name: "Home", path: "/" }, { name: "Glossary", path: "/glossary" }, { name: term.term }]}
      />
      <section className="max-w-3xl mx-auto px-6 py-16 lg:py-20">
        <Reveal>
          <div className="rounded-2xl border border-gray-200 bg-[#FAFAFA] p-8" data-testid="term-definition">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#0B63F6] mb-3">Definition</div>
            <p className="text-xl leading-relaxed text-[#111111]">{term.definition}</p>
          </div>
        </Reveal>
        {term.example && (
          <Reveal delay={0.05}>
            <div className="mt-8">
              <h2 className="font-display text-xl font-bold tracking-tight">Example</h2>
              <p className="text-lg text-gray-600 leading-relaxed mt-3">{term.example}</p>
            </div>
          </Reveal>
        )}

        {related.length > 0 && (
          <div className="mt-12">
            <h2 className="font-display text-xl font-bold tracking-tight mb-5">Related terms</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              {related.map((r) => (
                <Link key={r.slug} to={`/glossary/${r.slug}`} className="group rounded-xl border border-gray-200 p-5 hover:border-[#0B63F6] transition-colors">
                  <div className="font-display font-bold flex items-center justify-between">{r.term} <ArrowUpRight className="h-4 w-4 text-[#0B63F6]" /></div>
                  <p className="text-sm text-gray-600 mt-1 line-clamp-2">{r.definition}</p>
                </Link>
              ))}
            </div>
          </div>
        )}
      </section>
      <CtaSection heading="Put GEO into practice" sub="Talk to CompuBrain AI about building your AI visibility." />
    </>
  );
}
